import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import SidebarLayout from './SidebarLayout';
import Dashboard from './pages/Dashboard';
import SFInventory from './pages/SFInventory';
import Returns from './pages/Returns';
import UploadPo from './pages/UploadPo';
import Calendar from './pages/Calendar';
import Documents from './pages/Documents';
import Reports from './pages/Reports';
import Home from './pages/Home'; // Assuming Home component exists
import LoginPage from './pages/LoginPage'; // Create a LoginPage component
import ApiClient from './utils/ApiClient';
import PrivateRoute from './PrivateRoute';
import ReceiveInventory from "./pages/ReceiveInventory";
import {Bars3Icon} from "@heroicons/react/24/outline";
import PoList from "./pages/PoList";

import PackingList from "./pages/PacklingListList";
import SellerFulfilledOrders from "./pages/SellerFulfilledOrders"; // Utility to check authentication
import AdminSettings from "./pages/AdminSettings";
import PoInvoiceCreditList from "./pages/PoInvoiceCreditList";
import FeedStatus from "./pages/FeedStatus";
import LocalInventoryAdjustment from "./pages/LocalInventoryAdjustment";
import LocalInventoryAudit from "./pages/LocalInventoryAudit";
import LocalInventoryReport from "./pages/LocalInventoryReport";
import ManageSfpInventory from "./pages/ManageSfpInventory";
import IndexAdvisor from "./pages/IndexAdvisor";
import PriceLists from "./pages/PriceLists";
import CaseReview from "./pages/CaseReview";
import FbaFeeLookup from "./pages/FbaFeeLookup";
import AddAsinListings from "./pages/AddAsinListings";
import AuditReview from "./pages/AuditReview";
import Purchasing from "./pages/Purchasing";
import XeroDocuments from "./pages/XeroDocuments";
import XeroSettings from "./pages/XeroSettings";
import ShipmentPlanner from "./pages/ShipmentPlanner";
import ShipmentPlannerV2 from "./pages/ShipmentPlannerV2";
import ShipmentRuns from "./pages/ShipmentRuns";
import AsinAliases from "./pages/AsinAliases";
import SplitSets from "./pages/SplitSets";
import PlacementWorkspace from "./pages/PlacementWorkspace";
import ShipmentQueue from "./pages/ShipmentQueue";
import ConfirmedShipments from "./pages/ConfirmedShipments";
import BusinessInsights from "./pages/BusinessInsights";
import POCreation from "./pages/POCreation";
import ShippingCoach from "./pages/ShippingCoach";
import ProductSizeTier from "./pages/ProductSizeTier";
import AmazonAds from "./pages/AmazonAds";
import SellerLegalTracker from "./pages/SellerLegalTracker";
import SellerLegalDetail from "./pages/SellerLegalDetail";
import StatusPage from "./pages/StatusPage";
import WarRoom from "./components/WarRoom/WarRoom";
import PlanningPage from "./components/WarRoom/PlanningPage";
import FcLeaderboard from "./components/WarRoom/FcLeaderboard";
import BuyBoxCompare from "./components/WarRoom/BuyBoxCompare";
import AuthToast from './components/AuthToast';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import PlannerJobToaster from './components/Common/PlannerJobToaster';
import { Toaster } from 'react-hot-toast';

function App() {
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('info');
    const [showToast, setShowToast] = useState(false);
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(0);

    // Start automatic token refresh when app loads
    useEffect(() => {
        if (ApiClient.isAuthenticated()) {
            ApiClient.startTokenRefresh();
        }

        // Register notification callback
        ApiClient.setNotificationCallback((message, type = 'info') => {
            setToastMessage(message);
            setToastType(type);
            setShowToast(true);
        });

        // Register session timeout callback
        ApiClient.setSessionTimeoutCallback((secondsRemaining) => {
            setTimeoutCountdown(secondsRemaining);
            setShowTimeoutModal(true);
        });

        // Cleanup on unmount
        return () => {
            ApiClient.stopTokenRefresh();
        };
    }, []);

    const handleExtendSession = async () => {
        const success = await ApiClient.extendSession();
        if (success) {
            setShowTimeoutModal(false);
            setToastMessage('Session extended successfully');
            setToastType('success');
            setShowToast(true);
        } else {
            setToastMessage('Failed to extend session. Please log in again.');
            setToastType('error');
            setShowToast(true);
            setTimeout(() => {
                window.location.href = '/';
            }, 2000);
        }
    };

    const handleLogout = () => {
        ApiClient.clearAuth();
        setShowTimeoutModal(false);
        window.location.href = '/';
    };

    const handleToastClose = () => {
        setShowToast(false);
    };

    return (
        <Router>
            <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
            <PlannerJobToaster />
            <AuthToast
                show={showToast}
                message={toastMessage}
                type={toastType}
                onClose={handleToastClose}
            />
            <SessionTimeoutModal
                open={showTimeoutModal}
                countdown={timeoutCountdown}
                onExtendSession={handleExtendSession}
                onLogout={handleLogout}
            />
            <Routes>
                {/* Public routes */}
                <Route path="/" element={<LoginPage />} />
                <Route path="/fba-fee-lookup" element={<FbaFeeLookup />} />

                {/* Private routes wrapped in SidebarLayout */}
                <Route
                    path="/dashboard"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <Home />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/admin-dashboard"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <Home />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/merchantfulfilledinventory"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <SFInventory />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/returns"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <Returns />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/receiveinventory"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <ReceiveInventory />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/receiveinventory/uploadpo"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <UploadPo />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/polist"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <PoList />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/poinvoicecredits"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <PoInvoiceCreditList />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/packinglists"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <PackingList />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/sellerfulfilledorders"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <SellerFulfilledOrders />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />

                <Route
                    path="/documents"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <Documents />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                    path="/reports"
                    element={
                        <PrivateRoute>
                            <SidebarLayout>
                                <Reports />
                            </SidebarLayout>
                        </PrivateRoute>
                    }
                />
                <Route
                path="/admin/settings"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <AdminSettings />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/admin/status"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <StatusPage />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/feed-status"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <FeedStatus />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/localinventoryadjustment"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <LocalInventoryAdjustment />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/localinventoryaudit"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <LocalInventoryAudit />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/localinventoryreport"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <LocalInventoryReport />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/manage-sfp-inventory"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ManageSfpInventory />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/index-advisor"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <IndexAdvisor />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/price-lists"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <PriceLists />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/case-review"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <CaseReview />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/add-asin-listings"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <AddAsinListings />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/auditreview"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <AuditReview />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/purchasing"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <Purchasing />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/xero-documents"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <XeroDocuments />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/xero-settings"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <XeroSettings />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/shipment-planner"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ShipmentPlanner />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/shipment-planner-v2"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ShipmentPlannerV2 />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/asin-aliases"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <AsinAliases />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/shipment-runs"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ShipmentRuns />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/shipment-plans"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <PlacementWorkspace />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/split-sets"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <SplitSets />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/split-sets/:groupId"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <SplitSets />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/shipment-queue"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ShipmentQueue />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/confirmed-shipments"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ConfirmedShipments />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/business-insights"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <BusinessInsights />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/po-creation"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <POCreation />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/shipping-coach"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ShippingCoach />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/amazon-ads"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <AmazonAds />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/product-size"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <ProductSizeTier />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/seller-legal-tracker"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <SellerLegalTracker />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/seller-legal-tracker/:sellerId"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <SellerLegalDetail />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/war-room"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <WarRoom />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/war-room/planning"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <PlanningPage />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/war-room/fc-leaderboard"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <FcLeaderboard />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />
                <Route
                path="/war-room/bb-compare"
                element={
                    <PrivateRoute>
                        <SidebarLayout>
                            <BuyBoxCompare />
                        </SidebarLayout>
                    </PrivateRoute>
                }
            />

                {/* Catch-all route */}
                <Route path="*" element={<Navigate to="/login" />} />
            </Routes>
        </Router>
    );
}

export default App;
